import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { comics, comicTags } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

const bannerMap: Record<string, string> = {
  古言: "https://readdy.ai/api/search-image?query=%E8%8A%B1%E6%A0%91%E5%BA%AD%E9%99%A2%E6%9A%96%E8%89%B2%E8%B0%83%E5%8F%A4%E4%BB%A3%E6%89%8D%E5%AD%90%E4%BD%B3%E4%BA%BA%E4%B8%AD%E5%9B%BD%E6%B0%B4%E5%A2%A8%E7%94%BB%E6%BC%AB%E7%94%BB%E8%83%8C%E6%99%AF&width=1600&height=420&seq=cat-banner-guyan&orientation=landscape",
  宫斗: "https://readdy.ai/api/search-image?query=%E9%87%91%E9%94%A6%E5%AE%AB%E6%AE%BF%E6%9A%97%E7%BA%A2%E9%87%91%E8%89%B2%E8%B0%83%E5%8F%A4%E4%BB%A3%E5%AE%AB%E5%A6%83%E4%B8%AD%E5%9B%BD%E6%B0%B4%E5%A2%A8%E7%94%BB%E6%BC%AB%E7%94%BB%E8%83%8C%E6%99%AF&width=1600&height=420&seq=cat-banner-gongdou&orientation=landscape",
  权谋: "https://readdy.ai/api/search-image?query=%E6%A3%8B%E7%9B%98%E4%B8%8E%E5%AE%AB%E5%9F%8E%E9%98%B4%E5%BD%B1%E9%87%91%E9%BB%91%E8%89%B2%E8%B0%83%E4%B8%AD%E5%9B%BD%E6%B0%B4%E5%A2%A8%E7%94%BB%E6%BC%AB%E7%94%BB%E8%83%8C%E6%99%AF&width=1600&height=420&seq=cat-banner-quanmou&orientation=landscape",
  武侠: "https://readdy.ai/api/search-image?query=%E9%93%B6%E6%9C%88%E5%B1%B1%E5%B3%B0%E6%8F%90%E5%89%91%E4%BE%A0%E5%A3%AB%E5%86%B7%E8%89%B2%E8%B0%83%E4%B8%AD%E5%9B%BD%E6%B0%B4%E5%A2%A8%E7%94%BB%E6%BC%AB%E7%94%BB%E8%83%8C%E6%99%AF&width=1600&height=420&seq=cat-banner-wuxia&orientation=landscape",
  仙侠: "https://readdy.ai/api/search-image?query=%E4%BA%91%E7%AB%AF%E5%B1%B1%E6%B5%B7%E9%9D%92%E7%BB%BF%E9%87%91%E8%89%B2%E8%B0%83%E4%BF%AE%E4%BB%99%E4%B8%AD%E5%9B%BD%E6%B0%B4%E5%A2%A8%E7%94%BB%E6%BC%AB%E7%94%BB%E8%83%8C%E6%99%AF&width=1600&height=420&seq=cat-banner-xianxia&orientation=landscape",
  江湖: "https://readdy.ai/api/search-image?query=%E6%B2%99%E6%BC%A0%E8%A5%BF%E9%83%A8%E5%AE%A2%E6%A0%88%E6%9A%97%E9%BB%84%E8%93%9D%E8%B0%83%E4%B8%AD%E5%9B%BD%E6%B0%B4%E5%A2%A8%E7%94%BB%E6%BC%AB%E7%94%BB%E8%83%8C%E6%99%AF&width=1600&height=420&seq=cat-banner-jianghu&orientation=landscape",
};

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tag = searchParams.get("tag") ?? "全部";
  const q = searchParams.get("q") ?? "";
  const [sort, setSort] = useState("view");

  const filtered = comics.filter((c) => {
    const matchTag = tag === "全部" || c.tag === tag;
    const kw = q.trim();
    const matchQ = !kw || c.title.includes(kw) || c.author.includes(kw);
    return matchTag && matchQ;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "view") return Number(b.views.replace("万", "")) - Number(a.views.replace("万", ""));
    if (sort === "rate") return b.rating - a.rating;
    if (sort === "chapters") return b.chapters - a.chapters;
    return 0;
  });

  const setTag = (t: string) => {
    const params: Record<string, string> = {};
    if (t !== "全部") params.tag = t;
    if (q) params.q = q;
    setSearchParams(params);
  };

  const banner = bannerMap[tag];

  return (
    <div>
      {/* 页头横幅 */}
      <section className="relative w-full overflow-hidden">
        <div className="relative h-44 w-full md:h-56">
          {banner ? (
            <>
              <img src={banner} alt={`${tag}漫画分类横幅`} className="absolute inset-0 h-full w-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/75 to-foreground-950/30"></div>
            </>
          ) : (
            <div className="absolute inset-0 bg-foreground-950"></div>
          )}
          <div className="relative z-10 container-x flex h-full flex-col justify-center">
            <nav className="flex items-center gap-1.5 text-sm text-background-300" aria-label="面包屑">
              <button onClick={() => navigate("/")} className="cursor-pointer hover:text-background-50">首页</button>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-accent-300">{q ? "搜索结果" : `${tag}漫画`}</span>
            </nav>
            <h1 className="mt-2 font-heading text-3xl font-bold text-background-50 md:text-4xl">
              {q ? `“${q}”的搜索结果` : `${tag}漫画`}
            </h1>
            <p className="mt-1.5 text-sm text-background-200">
              {q ? `为你找到 ${sorted.length} 部相关漫画` : `古风漫画网精选 ${tag}题材国漫，分类直达、每日更新`}
            </p>
          </div>
        </div>
      </section>

      <div className="container-x py-10">
        {/* 题材筛选 */}
        <div className="flex flex-wrap gap-2">
          {["全部", ...comicTags].map((t) => (
            <button
              key={t}
              onClick={() => setTag(t)}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                tag === t ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {t === "全部" ? "全部" : `${t}`}
            </button>
          ))}
        </div>

        {/* 排序 + 数量 */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">
            共 <span className="font-bold text-primary-600">{sorted.length}</span> 部作品
          </p>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {[
              { key: "view", label: "按人气" },
              { key: "rate", label: "按评分" },
              { key: "chapters", label: "按章节" },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sort === s.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* 漫画网格 */}
        {sorted.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {sorted.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-16 flex flex-col items-center py-16 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-700">暂未找到相关作品</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或题材试试吧</p>
            <button onClick={() => { setSearchParams({}); setTag("全部"); }} className="mt-5 btn-primary">浏览全部漫画</button>
          </div>
        )}
      </div>
    </div>
  );
}